// Audio files from anywhere — the desktop, Finder, a browser's downloads, a
// folder of stems, a zip — dropped into the room. How most producers add a
// sound: drag the file in.
//
//   on a lane          the files go on that lane, one after another, from
//                      the beat they were dropped on (Live's drop on a track)
//   anywhere else      each file gets a track of its own, all starting at
//                      the insert marker (Live's drop below the tracks)
//
// Each file is kept like a recorded take (useTakeStore): uploaded to the jam
// in a connected room, held in this tab until a save in a local one. So it
// needs no account and no library quota — it is this project's audio, not a
// clip for later (that's a drop on the shelf's Mine).
//
// A file whose name or loop length states a tempo goes in warped at it, like
// a library clip; otherwise the room listens for one (detectTempo). Anything
// a bar or longer comes in warped regardless — Live's default — at the
// room's tempo when nothing clearer was heard, which stretches nothing until
// the tempo or the pitch moves (`autoWarpTempo`). Hits play at their own
// speed.

import { useCallback, useEffect, useRef, useState } from "react";

import type { Intent } from "@shared/sync/intents.js";
import type { CanonicalRoom } from "@shared/sync/state.js";
import { audioFilePlacement, placeLibraryClip, placeOnNewLane } from "@web-shared/lib/edits";
import { firstFreeSlot, snap } from "@web-shared/lib/placement";
import { autoWarpTempo, autoWarps, detectTempo } from "@web-shared/lib/warp";
import { guessClip } from "@/lib/web-upload/clip-meta";
import { IntakeError, intakeFromDataTransfer, intakeFromFileList, type Intake } from "@/lib/web-upload/intake";
import { trackNames } from "@/lib/web-upload/naming";
import { readFiles, type ReadFile } from "@/lib/web-upload/read-files";
import { decodeFile, toUploadBytes } from "@/lib/web-upload/to-wav";
import type { TakeStore } from "./useTakeStore";

/** What /api/jam/:id/audio takes (workers index.ts AUDIO_UPLOAD_MAX_BYTES). */
const MAX_FILE_BYTES = 96 * 1024 * 1024;
const DONE_NOTE_MS = 6000;

export interface AudioFileDrop {
  /** Files ArrangementView took on a lane (its onDropFiles). */
  dropOnLane: (laneId: string, atBeat: number, files: File[]) => void;
  /** A drop anywhere else in the room: a new track per file. Read `dt` now —
   * a DataTransfer is only readable during its event. */
  dropAnywhere: (dt: DataTransfer, atBeat: number) => void;
  /** Progress, or what happened. */
  note: { text: string; error: boolean } | null;
  dismissNote: () => void;
}

/** The tempo to warp a file at, or null to play it at its own speed
 * (`autoWarpTempo`); `heard` says whether that tempo came off the audio. */
async function tempoOf(f: ReadFile, roomTempo: number): Promise<{ bpm: number | null; heard: boolean }> {
  const stated = guessClip(f.file.name, f.probe.durationSec).bpm;
  let est = null;
  // Listen only where a tempo could matter: a hit at the room's tempo is
  // a hit at any tempo it could plausibly be heard at (60–200 BPM).
  if (stated === null && autoWarps((f.probe.durationSec * 200) / 60)) {
    try {
      est = detectTempo(await decodeFile(f.file.blob, f.file.ext, f.probe.sampleRate));
    } catch {
      est = null;
    }
  }
  const bpm = autoWarpTempo({ durationSec: f.probe.durationSec, stated, heard: est }, roomTempo);
  return { bpm, heard: stated === null && est !== null && bpm === est.bpm };
}

export function useAudioFileDrop({
  room,
  editor,
  takes,
  tempo,
  grid,
}: {
  room: CanonicalRoom | null;
  editor: { propose: (intents: Intent[]) => void };
  takes: TakeStore;
  tempo: number;
  grid: number;
}): AudioFileDrop {
  const [note, setNote] = useState<AudioFileDrop["note"]>(null);
  // Uploads take a while: place against the room as it is THEN, not as it
  // was when the files were dropped (a peer, or the previous file, moved it).
  const roomRef = useRef(room);
  roomRef.current = room;
  const tempoRef = useRef(tempo);
  tempoRef.current = tempo;
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (doneTimer.current) clearTimeout(doneTimer.current);
  }, []);

  const add = useCallback(
    async (intake: Promise<Intake>, laneId: string | null, atBeat: number) => {
      if (doneTimer.current) clearTimeout(doneTimer.current);
      setNote({ text: "Reading the files…", error: false });
      let files: ReadFile[];
      let unreadable: number;
      try {
        const got = await intake;
        const read = await readFiles(got.files);
        files = read.ok;
        unreadable = read.bad.length;
      } catch (err) {
        setNote({ text: err instanceof IntakeError ? err.message : "Those files couldn't be opened.", error: true });
        return;
      }
      if (files.length === 0) {
        setNote({ text: "No audio in that drop. WAV, AIFF, FLAC and MP3 work.", error: true });
        return;
      }

      const names = trackNames(files.map((f) => f.file.name));
      const baseOrd = (roomRef.current?.tracks ?? []).reduce((m, t) => Math.max(m, t.ord), -1) + 1;
      let at = Math.max(0, snap(atBeat, grid));
      let placed = 0;
      const warped: string[] = [];
      const problems: string[] = [];
      for (const [i, f] of files.entries()) {
        const name = names[i] ?? f.file.name;
        setNote({ text: files.length === 1 ? `Adding ${name}…` : `Adding ${i + 1} of ${files.length}: ${name}…`, error: false });
        try {
          const bytes = await toUploadBytes(f.file.blob, f.file.ext, f.probe);
          if (bytes.byteLength > MAX_FILE_BYTES) {
            problems.push(`${name} is too long (over 96 MB as WAV)`);
            continue;
          }
          const [key, { bpm, heard }] = await Promise.all([
            takes.put(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer),
            tempoOf(f, tempoRef.current),
          ]);
          const now = roomRef.current;
          if (!now) return;
          const placement = audioFilePlacement({ audioKey: key, name, durationSec: f.probe.durationSec, bpm }, tempoRef.current);
          const lane = laneId ? now.tracks.find((t) => t.id === laneId) : undefined;
          if (lane && (lane.trackKind ?? "audio") === "audio") {
            const intent = placeLibraryClip(placement, lane.id, at, (start, duration) => firstFreeSlot(now, lane.id, start, duration));
            if (!intent || intent.kind !== "upsertClip") {
              problems.push(`no room on the lane for ${name}`);
              continue;
            }
            editor.propose([intent]);
            at = intent.clip.startTime + placement.clip.duration;
          } else {
            // A MIDI lane can't hold audio: a track of its own, like a drop below.
            const intents = placeOnNewLane(placement, name, baseOrd + i, at);
            if (intents.length === 0) {
              problems.push(`${name} couldn't be placed`);
              continue;
            }
            editor.propose(intents);
          }
          placed++;
          // Only a tempo READ off the file is worth a note: a long file warped
          // at the room's tempo plays exactly as it is until something moves.
          if (bpm !== null && (heard || bpm !== tempoRef.current)) warped.push(`${name} at ${Math.round(bpm * 100) / 100} BPM`);
        } catch (err) {
          problems.push(`${name}: ${err instanceof Error ? err.message : "couldn't be added"}`);
        }
      }

      const parts: string[] = [];
      if (placed > 0) parts.push(`Added ${placed} ${placed === 1 ? "file" : "files"}.`);
      if (warped.length > 0) parts.push(`Warped ${warped.join(", ")} — the clip editor fixes a misread tempo.`);
      if (unreadable > 0) parts.push(`${unreadable} ${unreadable === 1 ? "file wasn't" : "files weren't"} readable audio.`);
      if (problems.length > 0) parts.push(`Not added: ${problems.join("; ")}.`);
      const error = placed === 0 || problems.length > 0;
      setNote({ text: parts.join(" "), error });
      if (!error && warped.length === 0) doneTimer.current = setTimeout(() => setNote(null), DONE_NOTE_MS);
    },
    [editor, takes, grid],
  );

  const dropOnLane = useCallback(
    (laneId: string, atBeat: number, files: File[]) => void add(intakeFromFileList(files), laneId, atBeat),
    [add],
  );
  const dropAnywhere = useCallback(
    (dt: DataTransfer, atBeat: number) => void add(intakeFromDataTransfer(dt), null, atBeat),
    [add],
  );
  const dismissNote = useCallback(() => setNote(null), []);

  return { dropOnLane, dropAnywhere, note, dismissNote };
}
